import { useState } from "react";
import { Folder as FolderIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatFileSize } from "@/lib/files-format";
import { FileActionsMenu } from "./FileActionsMenu";
import { DRAG_ITEM_MIME, fileIconFor, type DraggedItem, type FileRow, type FolderRow } from "./files-types";

type FixedEntry = { key: string; label: string; icon: typeof FolderIcon; onClick: () => void };

type Actions = {
  onOpenFolder: (folder: FolderRow) => void;
  onOpenFile: (file: FileRow) => void;
  onRenameFolder: (folder: FolderRow) => void;
  onMoveFolder: (folder: FolderRow) => void;
  onDeleteFolder: (folder: FolderRow) => void;
  onDownloadFile: (file: FileRow) => void;
  onTagsFile: (file: FileRow) => void;
  onShareFile: (file: FileRow) => void;
  onHistoryFile: (file: FileRow) => void;
  onRenameFile: (file: FileRow) => void;
  onMoveFile: (file: FileRow) => void;
  onDeleteFile: (file: FileRow) => void;
  /** Soltou um item (arquivo ou pasta) arrastado em cima de outra pasta — move pra lá. Opcional:
   *  contextos sem pastas reais (ex: arquivos de uma entidade) não precisam implementar. */
  onDropItemOnFolder?: (item: DraggedItem, folder: FolderRow) => void;
  /** Soltou arquivo(s) do sistema operacional direto em cima de uma pasta — sobe já dentro dela. */
  onDropFilesOnFolder?: (folder: FolderRow, files: FileList) => void;
};

export function FilesGrid({
  fixedEntries,
  folders,
  files,
  actions,
}: {
  fixedEntries?: FixedEntry[];
  folders: FolderRow[];
  files: FileRow[];
  actions: Actions;
}) {
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  function handleFolderDragOver(e: React.DragEvent, folderId: string) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folderId);
  }

  function handleFolderDrop(e: React.DragEvent, folder: FolderRow) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
    if (e.dataTransfer.types.includes("Files")) {
      if (e.dataTransfer.files.length > 0) actions.onDropFilesOnFolder?.(folder, e.dataTransfer.files);
      return;
    }
    const raw = e.dataTransfer.getData(DRAG_ITEM_MIME);
    if (!raw) return;
    const item = JSON.parse(raw) as DraggedItem;
    if (item.kind === "pasta" && item.id === folder.id) return; // não solta em cima de si mesma
    actions.onDropItemOnFolder?.(item, folder);
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {fixedEntries?.map((entry) => (
        <button
          key={entry.key}
          type="button"
          onClick={entry.onClick}
          className="flex flex-col items-center gap-2 rounded-xl border border-zinc-200 bg-white p-4 text-center shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/50 dark:border-border dark:bg-card dark:hover:bg-primary/5"
        >
          <entry.icon className="h-8 w-8 text-brand-500 dark:text-primary" />
          <span className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">{entry.label}</span>
        </button>
      ))}

      {folders.map((folder) => (
        <div
          key={folder.id}
          onClick={() => actions.onOpenFolder(folder)}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(DRAG_ITEM_MIME, JSON.stringify({ kind: "pasta", id: folder.id, name: folder.name }));
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(e) => handleFolderDragOver(e, folder.id)}
          onDragLeave={() => setDragOverFolderId((prev) => (prev === folder.id ? null : prev))}
          onDrop={(e) => handleFolderDrop(e, folder)}
          className={cn(
            "group relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border p-4 text-center shadow-sm transition-colors",
            dragOverFolderId === folder.id
              ? "border-brand-400 bg-brand-50 dark:border-primary/60 dark:bg-primary/10"
              : "border-zinc-200 bg-white hover:border-brand-300 hover:bg-brand-50/50 dark:border-border dark:bg-card dark:hover:bg-primary/5",
          )}
        >
          <div className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100">
            <FileActionsMenu
              onRename={() => actions.onRenameFolder(folder)}
              onMove={() => actions.onMoveFolder(folder)}
              onDelete={() => actions.onDeleteFolder(folder)}
            />
          </div>
          <FolderIcon className="h-8 w-8 fill-amber-100 text-amber-500 dark:fill-amber-500/20 dark:text-amber-400" />
          <span className="w-full truncate text-sm font-medium text-zinc-900 dark:text-foreground">{folder.name}</span>
        </div>
      ))}

      {files.map((file) => {
        const Icon = fileIconFor(file.contentType);
        return (
          <div
            key={file.id}
            onClick={() => actions.onOpenFile(file)}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_ITEM_MIME, JSON.stringify({ kind: "arquivo", id: file.id, name: file.name }));
              e.dataTransfer.effectAllowed = "move";
            }}
            className="group relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-zinc-200 bg-white p-4 text-center shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/50 dark:border-border dark:bg-card dark:hover:bg-primary/5"
          >
            <div className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100">
              <FileActionsMenu
                onDownload={() => actions.onDownloadFile(file)}
                onTags={() => actions.onTagsFile(file)}
                onShare={() => actions.onShareFile(file)}
                onHistory={() => actions.onHistoryFile(file)}
                onRename={() => actions.onRenameFile(file)}
                onMove={() => actions.onMoveFile(file)}
                onDelete={() => actions.onDeleteFile(file)}
              />
            </div>
            <Icon className="h-8 w-8 text-zinc-400 dark:text-muted-foreground" />
            <span className="w-full truncate text-sm font-medium text-zinc-900 dark:text-foreground" title={file.name}>
              {file.name}
            </span>
            <span className="text-[11px] text-zinc-400 dark:text-muted-foreground">{formatFileSize(file.size)}</span>
          </div>
        );
      })}
    </div>
  );
}
